import { t } from 'i18next'
import { List } from 'immutable'
import React from 'react'
import { InView } from 'react-intersection-observer'
import { AsyncState, Link, Updater, Widget, async, fromJSX, loadingAsyncState } from 'widgets-for-react'
import { blockInviewHandler } from '../../drupal/rendering/inview_utils.mod'
import { PaginatedItem } from '../../drupal/types_and_parser/extra_types.mod'
import { SearchPageParams } from '../route/state'
import { Pagination } from '../templates/pagination.mod'

export type SearchOverviewState = {
  overview_page: number
  current_query: string
  get_searches: AsyncState<PaginatedItem<SearchResultItem>>
}

export type SearchResultItem = {
  title: string
  url: string
  content: {
    title: string[]
    intro: string[]
  }
  introduction: string
  bundle: string
}

export const search = (
  query: string,
  bundle: string,
  page: number = 0,
  pagesize: number = 10,
  lang: string
): AsyncState<PaginatedItem<SearchResultItem>> =>
  loadingAsyncState((_) =>
    fetch(
      `/api/v1/search?query=${query}&lang=${lang == 'es' ? 'ib' : lang}&page=${page}&pagesize=${pagesize}${
        bundle ? `&bundle=${bundle}` : ''
      }`,
      { credentials: 'include' }
    ).then((res) => {
      if (!res.ok)
        return {
          kind: 'failed',
          status: 500
        }

      window.dataLayer.push({
        event: 'event',
        category: 'Search',
        action: 'Search',
        label: query
      })

      return res.json().then((json) => ({
        kind: 'result',
        status: 200,
        value: {
          items: List(json.Results),
          pager: {
            current_page: page,
            items_per_page: pagesize,
            total_items: json.Count,
            total_pages: Math.ceil(json.Count / pagesize)
          }
        }
      }))
    })
  )

export const search_overview = (r: [SearchPageParams, SearchOverviewState]): Widget<Updater<SearchOverviewState>> =>
  fromJSX((setState) => {
    const pageName = r[0].slug
    let ss = r[1]
    const query: string = r[0].query
    const lang: string = r[0].lang

    const setPage = (page: number) => {
      window.history.pushState({}, '', `/${lang}/${pageName}/${r[1].current_query}/${page}`)
      setState((s) => ({
        ...s,
        overview_page: page,
        get_searches: search(r[1].current_query, '', page, 10, lang)
      }))
    }

    const setQuery = (query: string) => {
      setState((s) => ({
        ...s,
        current_query: query
      }))
    }

    const runQuery = () => {
      window.history.pushState({}, '', `/${lang}/${pageName}/${query}/${0}`)
    }

    // Anything that is not 'loaded' renders the same empty body: searchOverview guards on
    // !page.items and on page.pager, so the empty object 'failed' has always passed works
    // for every other state too. The previous `null` fallback still reached searchOverview
    // for the unloaded, connection-error and unauthorized states and threw on page.items.
    const searchResult =
      ss.get_searches.kind == 'loaded' ? ss.get_searches.value : ({} as PaginatedItem<SearchResultItem>)

    r[1] = ss

    return (
      <main id="a11y-main" className="main main--search-results">
        <InView
          as="header"
          onChange={(inView) => blockInviewHandler('pageHeader', inView, 'io-page-header', 'inview')}
          triggerOnce={true}
          threshold={0}
          id="io-page-header"
          className="pageheader pageheader--flat no-image"
        >
          <div className="pageheader__inner">
            <div className="pageheader__content container container--m">
              <div className="pageheader__message">
                <div className="pageheader__header">
                  <h1 className="pageheader__title">{t('search:search')}</h1>
                </div>
              </div>
            </div>
          </div>
        </InView>
        <div className="search-results">
          {async(ss.get_searches, undefined, {
            connectionError: fromJSX(() => <div>Connection error.</div>),
            otherError: fromJSX(() => (
              <section className="search-results__section container container--m grid--gutter">
                <h1 className="search-results__title title--m">Error</h1>
              </section>
            )),
            loading: () => fromJSX(() => <></>)
          }).run((res) =>
            setState((s) => {
              let s1 = res(s.get_searches)
              if (s1.kind == 'loaded') {
                if (s1.value.items.count() > 0) {
                  window.dataLayer.push({
                    event: 'event.refresco',
                    category: 'Search',
                    action: 'Result',
                    label: s.current_query
                  })
                } else {
                  window.dataLayer.push({
                    event: 'event.refresco',
                    category: 'Search',
                    action: 'No result',
                    label: s.current_query
                  })
                }
              }

              return { ...s, get_searches: s1 }
            })
          )}
          {ss.get_searches.kind != 'loading' ? (
            searchOverview(searchResult, setPage, setQuery, runQuery, ss.overview_page, ss.current_query, lang)
          ) : (
            <section className="search-results__section container container--m grid--block">
              <div className="loader">
                <div className="loader__inner"></div>
              </div>
            </section>
          )}
        </div>
      </main>
    )
  })

const searchOverview = (
  page: PaginatedItem<SearchResultItem>,
  setPage: (page: number) => void,
  setQuery: (query: string) => void,
  runQuery: () => void,
  current_page: number,
  current_query: string,
  lang: string
) => {
  return (
    <section className="block block--search-results" key={'search-overview'}>
      <div className="block__inner grid--spacing">
        <div className="block__content container container--m grid--gutter">
          <h2>{t('search:search')}</h2>

          <form
            onSubmit={(e) => {
              e.preventDefault()
              location.href = `/${lang}/search/${current_query}/0`
            }}
            className="search-results__form"
          >
            <input
              className="search-results__input"
              autoFocus
              type="search"
              value={current_query}
              onChange={(e) => setQuery(e.currentTarget.value)}
            />
          </form>
          {!page.items || page.items.count() == 0 ? (
            <p className="search-results__text">{t('search:no_results')}</p>
          ) : (
            <ul className="search-results__list">
              {page.items.toArray().map((item) => searchItem(item))}
            </ul>
          )}
          {page.pager && <Pagination pager={page.pager} setPage={setPage} />}
        </div>
      </div>
    </section>
  )
}

export const searchItem = (search_item: SearchResultItem) => {
  return (
    <li className="search-results__item result">
      {/* TO DO: Properly fix languages for NA */}
      <Link
        to={
          window.location.pathname?.startsWith('/na/')
            ? search_item.url.replace('/en/', '/na/')
            : window.location.pathname?.startsWith('/uk/')
            ? search_item.url.replace('/en/', '/uk/')
            : search_item.url
        }
        className="result__link"
      >
        {/* TO DO: add content type in "result__label" below */}
        <div className="result__message">
          <h3 className="result__title title--s">{search_item.title}</h3>
          <p
            className="result__intro"
            dangerouslySetInnerHTML={{
              __html: (Object.keys(search_item.content) as Array<keyof SearchResultItem['content']>)
                .map((key) => search_item.content[key]?.join(' (...) '))
                .join(' (...) ')
            }}
          ></p>
        </div>
      </Link>
    </li>
  )
}
